// src/hooks/useGroceryList.js  —  ohapotato v2026.10.36
//
// Simple grocery list for ALL users (free + premium).
// Items are plain strings (ingredient names).
// Deduplication is enforced — same name (case-insensitive) never added twice.
// Persisted to localStorage under 'ohp-grocery'.

import { useState, useCallback, useEffect } from 'react';

const KEY = 'ohp-grocery';

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
    // Normalise: support both legacy {name, checked} objects and plain strings
    return raw.map(r => typeof r === 'string'
      ? { name: r, checked: false }
      : { name: r.name || r, checked: !!r.checked }
    );
  } catch { return []; }
}

function save(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
}

export function useGroceryList() {
  const [items, setItems] = useState(load);

  useEffect(() => { save(items); }, [items]);

  // Add one item — silently skips exact duplicates (case-insensitive)
  const addItem = useCallback((name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return false;
    setItems(prev => {
      const exists = prev.some(i => i.name.toLowerCase() === trimmed.toLowerCase());
      if (exists) return prev;
      return [...prev, { name: trimmed, checked: false }];
    });
    return true;
  }, []);

  // Add multiple items at once (e.g. from meal planner), skip all duplicates
  const addItems = useCallback((names) => {
    const trimmed = (names || []).map(n => (n || '').trim()).filter(Boolean);
    if (!trimmed.length) return;
    setItems(prev => {
      const existing = new Set(prev.map(i => i.name.toLowerCase()));
      const toAdd    = trimmed.filter(n => !existing.has(n.toLowerCase()));
      return toAdd.length ? [...prev, ...toAdd.map(n => ({ name: n, checked: false }))] : prev;
    });
  }, []);

  const toggleItem  = useCallback((name) =>
    setItems(prev => prev.map(i => i.name === name ? { ...i, checked: !i.checked } : i)), []);

  const removeItem  = useCallback((name) =>
    setItems(prev => prev.filter(i => i.name !== name)), []);

  const clearDone   = useCallback(() =>
    setItems(prev => prev.filter(i => !i.checked)), []);

  const clearAll    = useCallback(() => setItems([]), []);

  return { items, addItem, addItems, toggleItem, removeItem, clearDone, clearAll };
}
